import { APPEARANCE_THEME_OPTIONS, getAppearanceThemeLabel } from "providers/appearance/constants";
import {
  getSolarPhaseConfig,
  SOLAR_CYCLE_CLOCK_ORDER,
  type SolarCycleBlend,
  type SolarPhase,
} from "providers/appearance/solar-cycle";
import { cn } from "utils/cn";

type SolarCycleDayPanelProps = {
  phase: SolarPhase;
  resolvedMode: "light" | "dark";
  blend: SolarCycleBlend | null;
};

function isBlendActive(blend: SolarCycleBlend | null): blend is SolarCycleBlend {
  return blend !== null && blend.progress > 0 && blend.progress < 1;
}

export const SolarCycleDayPanel = ({ phase, resolvedMode, blend }: SolarCycleDayPanelProps) => {
  const activeConfig = getSolarPhaseConfig(phase);
  const blending = isBlendActive(blend);

  return (
    <div className="flex flex-col gap-2.5 rounded-lg border border-border bg-muted/30 p-3">
      <p className="text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">
        Through your day
      </p>

      <div
        className="h-1.5 overflow-hidden rounded-full bg-gradient-to-r from-zinc-700 via-amber-200 to-zinc-800 dark:from-zinc-950 dark:via-amber-900/60 dark:to-zinc-900"
        aria-hidden
      />

      <div className="grid grid-cols-6 gap-1">
        {SOLAR_CYCLE_CLOCK_ORDER.map((phaseId) => {
          const config = getSolarPhaseConfig(phaseId);
          const preview = APPEARANCE_THEME_OPTIONS.find((option) => option.id === config.theme)
            ?.preview[config.mode];
          const isActive = phaseId === phase;
          const isTransitionEndpoint =
            blending && (phaseId === blend.from.phase || phaseId === blend.to.phase);

          return (
            <div key={phaseId} className="flex flex-col items-center gap-1">
              <span
                className={cn(
                  "rounded-full ring-1 ring-black/10 dark:ring-white/15",
                  isActive || isTransitionEndpoint ? "size-3 ring-2 ring-primary/40" : "size-2.5"
                )}
                style={{ backgroundColor: preview?.selected }}
                aria-hidden
              />
              <span
                className={cn(
                  "text-center text-[9px] font-medium leading-tight",
                  isActive || isTransitionEndpoint ? "text-foreground" : "text-muted-foreground/70"
                )}
              >
                {config.label}
              </span>
            </div>
          );
        })}
      </div>

      <p className="text-[11px] leading-snug text-muted-foreground">
        {blending ? (
          <>
            Transitioning:{" "}
            <span className="font-medium text-foreground">
              {getSolarPhaseConfig(blend.from.phase).label} ·{" "}
              {getAppearanceThemeLabel(blend.from.theme)}
            </span>
            <span className="text-muted-foreground/80"> → </span>
            <span className="font-medium text-foreground">
              {getSolarPhaseConfig(blend.to.phase).label} ·{" "}
              {getAppearanceThemeLabel(blend.to.theme)}
            </span>
          </>
        ) : (
          <>
            Current phase:{" "}
            <span className="font-medium text-foreground">
              {activeConfig.label} · {getAppearanceThemeLabel(activeConfig.theme)}
            </span>
          </>
        )}
        <span className="text-muted-foreground/80"> · {resolvedMode} mode</span>
      </p>
      <p className="text-[11px] leading-snug text-muted-foreground">
        Uses your device&apos;s local clock to shift palettes gradually through the day.
      </p>
    </div>
  );
};
